import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { setSnackbar } from "../../snackbar/api/snackbar";
import { saveInternalUser } from "../api/actions";
import { ROLES, canManagePeople, flag } from "../accessRoles";

const EDITABLE_ROLES = ROLES.filter((r) => r.key !== "isSuperUser");

const formFor = (person) => ({
  isActive: flag(person?.isActive),
  ...Object.fromEntries(EDITABLE_ROLES.map((r) => [r.key, flag(person?.[r.key])])),
});

function PersonDrawer({ person, me, onClose, saveInternalUser, setSnackbar }) {
  const [form, setForm] = useState(formFor(person));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(formFor(person));
    setError(null);
  }, [person?.uuid]);

  if (!person) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const isMe = person.uuid === me?.uuid;
  const superAdmin = flag(person.isSuperUser);
  // Only a super admin can change a super admin, and nobody can change their own access.
  const canEdit = canManagePeople(me) && !isMe && (!superAdmin || flag(me?.isSuperUser));
  const lockedReason = isMe
    ? "You can't change your own access. Ask another admin."
    : superAdmin && !flag(me?.isSuperUser)
      ? "Only a super admin can change a super admin."
      : null;
  const changed = JSON.stringify(form) !== JSON.stringify(formFor(person));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const toNum = (b) => (b ? 1 : 0);
      await saveInternalUser(person.uuid, {
        isActive: toNum(form.isActive),
        ...Object.fromEntries(EDITABLE_ROLES.map((r) => [r.key, toNum(form[r.key])])),
      });
      setSnackbar(true, "success", `${person.name} updated`);
      onClose();
    } catch (e) {
      setError(`Couldn't save ${person.name}. Try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={Boolean(person)} onClose={saving ? undefined : onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" noWrap>{person.name}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap>{person.email}</Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label="Close"><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 2.5, display: "grid", gap: 2.5, overflowY: "auto" }}>
        <Typography variant="body2" color="text.secondary">
          {person.lastLogin ? `Last signed in ${moment(person.lastLogin).format("MMMM D, YYYY")}.` : "Has never signed in."}
        </Typography>

        {lockedReason && canManagePeople(me) && <Alert severity="info">{lockedReason}</Alert>}

        {person.service && (
          <Alert severity="warning">This looks like a service account used by the nightly batch or system jobs. Making it inactive can stop them.</Alert>
        )}

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                disabled={!canEdit || saving}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                color="success"
              />
            }
            label={<Typography sx={{ fontWeight: 500 }}>Active</Typography>}
          />
          <Typography variant="caption" color="text.secondary" component="div">
            Inactive people can't sign in to Ubil+. A session that's already open isn't ended.
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>Roles</Typography>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Super admin: {superAdmin ? "Yes" : "No"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {superAdmin ? "Super admins can open everything below." : "Super admin can't be granted from this page."}
            </Typography>
          </Box>
          {EDITABLE_ROLES.map((role) => (
            <FormControlLabel
              key={role.key}
              sx={{ alignItems: "flex-start", m: 0 }}
              control={
                <Checkbox
                  checked={superAdmin || form[role.key]}
                  disabled={!canEdit || superAdmin || saving}
                  onChange={(e) => setForm({ ...form, [role.key]: e.target.checked })}
                  sx={{ pt: 0.25 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{role.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{role.opens}</Typography>
                </Box>
              }
            />
          ))}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {canEdit && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" color="secondary" onClick={save} disabled={!changed || saving} sx={{ textTransform: "none" }}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">Role changes take effect the next time {person.firstName || "they"} signs in.</Typography>
      </Box>
    </Drawer>
  );
}

export default connect(null, { saveInternalUser, setSnackbar })(PersonDrawer);
